import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  LayoutGrid,
  Info,
  LogOut,
  ClipboardList,
  UtensilsCrossed,
  Bot,
  Users,
  Moon,
  Sun,
  ShoppingBag,
  ShoppingCart,
  Download,
  MoreHorizontal,
  ChevronRight,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigationBlocker } from "@/hooks/useNavigationBlocker";
import { useTheme } from "@/hooks/useTheme";
import { useCart } from "@/hooks/useCart";
import BrandLogo from "@/components/BrandLogo";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const ADMIN_EMAIL = "sanapilates.sn@gmail.com";

interface NavbarProps {
  user: any;
  children?: React.ReactNode;
}

const mainNavItems = [
  { path: "/", label: "Inicio", icon: CalendarDays },
  { path: "/programa", label: "Programa", icon: LayoutGrid },
  { path: "/tu-plan", label: "Mi Progreso", icon: ClipboardList },
];

const extraNavItems = [
  { path: "/recetas", label: "Recetas", icon: UtensilsCrossed },
  { path: "/coach", label: "Tu Coach", icon: Bot },
  { path: "/recursos", label: "Recursos", icon: ShoppingBag },
  { path: "/info", label: "Contacto", icon: Info },
  { path: "/ayuda", label: "Instalar", icon: Download },
];

const TopBar = ({ user, showSidebarTrigger = false }: { user: any; showSidebarTrigger?: boolean }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { tryNavigate } = useNavigationBlocker();
  const { isDark, toggle } = useTheme();
  const { count } = useCart();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-md">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        {showSidebarTrigger && <SidebarTrigger className="mr-2" />}
        <button
          onClick={() => tryNavigate("/", () => navigate("/"))}
          className="flex items-center"
          aria-label="Ir al inicio"
        >
          <BrandLogo imageClassName="h-10 sm:h-11" />
        </button>
        <div className="flex-1" />
        <div className="flex items-center gap-1">
          <button
            onClick={() => tryNavigate("/carrito", () => navigate("/carrito"))}
            className="relative flex items-center justify-center rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Carrito"
          >
            <ShoppingCart className="h-4 w-4" />
            {count > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-sm font-semibold text-primary-foreground">
                {count}
              </span>
            )}
          </button>
          <button
            onClick={toggle}
            className="flex items-center justify-center rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={isDark ? "Modo claro" : "Modo oscuro"}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user && (
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Salir</span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

const bottomNavItems = [
  { path: "/", label: "Inicio", icon: CalendarDays },
  { path: "/programa", label: "Programa", icon: LayoutGrid },
  { path: "/tu-plan", label: "Progreso", icon: ClipboardList },
  { path: "/recetas", label: "Recetas", icon: UtensilsCrossed },
];

const moreNavItems = [
  { path: "/coach", label: "Tu Coach", icon: Bot },
  { path: "/recursos", label: "Recursos", icon: ShoppingBag },
  { path: "/info", label: "Ayuda y contacto", icon: Info },
  { path: "/ayuda", label: "Instalar la app", icon: Download },
];

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tryNavigate } = useNavigationBlocker();
  const [moreOpen, setMoreOpen] = useState(false);
  const isMoreActive = moreNavItems.some((item) => location.pathname === item.path);

  const goTo = (path: string) => {
    setMoreOpen(false);
    tryNavigate(path, () => navigate(path));
  };

  return (
    <div className="safe-area-bottom pointer-events-none fixed bottom-[18px] left-[14px] right-[14px] z-50 md:hidden">
      <div className="pointer-events-auto mx-auto grid h-[76px] max-w-xl grid-cols-5 items-stretch rounded-[38px] border border-card/90 bg-card/85 p-1.5 shadow-[0_14px_34px_hsl(var(--foreground)/0.14)] backdrop-blur-[14px]">
        {bottomNavItems.map(({ path, label, icon: Icon }) => {
          const isActive = location.pathname === path;

          return (
            <button
              key={path}
              onClick={() => tryNavigate(path, () => navigate(path))}
              className={`mx-0.5 flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-[24px] px-0.5 py-1.5 transition-colors ${
                isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className={`h-[22px] w-[22px] ${isActive ? "text-primary" : ""}`} />
              <span className={`text-sm font-medium leading-tight tracking-tight ${isActive ? "text-primary" : ""}`}>{label}</span>
            </button>
          );
        })}
        <button
          onClick={() => setMoreOpen(true)}
          className={`flex flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 transition-colors ${
            isMoreActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
          }`}
          aria-label="Más opciones"
        >
          <MoreHorizontal className="h-[22px] w-[22px]" />
          <span className="text-sm font-medium leading-tight tracking-tight">Más</span>
        </button>
      </div>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-[34px] border-card/90 bg-card/95 px-4 pb-6 pt-4 backdrop-blur-[14px]">
          <SheetHeader className="sr-only">
            <SheetTitle>Más opciones</SheetTitle>
            <SheetDescription>Navegación adicional</SheetDescription>
          </SheetHeader>
          {moreNavItems.map(({ path, label, icon: Icon }) => {
            const isActive = location.pathname === path;

            return (
              <button
                key={path}
                onClick={() => goTo(path)}
                className={`flex min-h-[64px] w-full items-center gap-3 rounded-[22px] px-3 text-[17px] font-extrabold transition-colors ${
                  isActive ? "bg-muted text-primary" : "text-foreground hover:bg-muted"
                }`}
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="flex-1 text-left">{label}</span>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </button>
            );
          })}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const DesktopSidebar = ({ isAdmin }: { isAdmin: boolean }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tryNavigate } = useNavigationBlocker();

  return (
    <Sidebar collapsible="icon" className="hidden md:flex">
      <SidebarContent className="pt-4">
        <SidebarGroup>
          <SidebarGroupLabel>Programa</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map(({ path, label, icon: Icon }) => (
                <SidebarMenuItem key={path}>
                  <SidebarMenuButton
                    isActive={location.pathname === path}
                    onClick={() => tryNavigate(path, () => navigate(path))}
                    tooltip={label}
                    className="h-11"
                  >
                    <Icon className="h-5 w-5" />
                    <span>{label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Bienestar</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {extraNavItems.map(({ path, label, icon: Icon }) => (
                <SidebarMenuItem key={path}>
                  <SidebarMenuButton
                    isActive={location.pathname === path}
                    onClick={() => tryNavigate(path, () => navigate(path))}
                    tooltip={label}
                    className="h-11"
                  >
                    <Icon className="h-5 w-5" />
                    <span>{label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {isAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={location.pathname === "/usuarios"}
                    onClick={() => tryNavigate("/usuarios", () => navigate("/usuarios"))}
                    tooltip="Usuarios"
                    className="h-11"
                  >
                    <Users className="h-5 w-5" />
                    <span>Usuarios</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};

const Navbar = ({ user, children }: NavbarProps) => {
  const isMobile = useIsMobile();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user?.email?.toLowerCase() === ADMIN_EMAIL) {
      setIsAdmin(true);
    }
  }, [user]);

  if (isMobile) {
    return (
      <>
        <TopBar user={user} />
        <main className="pb-28">{children}</main>
        <BottomNav />
      </>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <DesktopSidebar isAdmin={isAdmin} />
        <div className="flex flex-1 flex-col">
          <TopBar user={user} showSidebarTrigger />
          <main className="flex-1">{children}</main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Navbar;
